import { use, Suspense, type ReactNode } from 'react'
import { Link } from 'react-router'
import { CompanySuspensionContext } from '@/components/company-suspension/context'
import { QueryErrorBoundary } from '@/components/shared/error-boundary-overlay/query-error-boundary'
import { ServiceCard } from '@/components/sv'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { isPresent } from '@/lib/utils'
import { RecentServicesFallback } from '@/routes/home/recent-services/fallback'
import {
  RECENT_SERVICES_DISPLAY_COUNT,
  RECENT_SERVICES_QUERY,
} from '@/routes/home/recent-services/query'
import {
  getAppInfo,
  getStaticSiteInfo,
  DatabaseInfo,
} from '@/routes/home/recent-services/utils'
import type {
  RecentServicesQuery,
  RecentServicesQueryVariables,
} from '@/routes/home/recent-services/query.types.gen'

const RecentServicesGrid = ({ children }: { children: ReactNode }) => {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
}

const RecentServicesData = () => {
  const { idCompany } = useIdCompany()
  const { getCompanyPath } = useCompanyPath()
  const { isSuspended: isCompanySuspended } = use(CompanySuspensionContext)
  const { data } = useSuspenseQuery<
    RecentServicesQuery,
    RecentServicesQueryVariables
  >(RECENT_SERVICES_QUERY, {
    variables: {
      idCompany,
      serviceOrderBy: { updatedAt: 'desc' },
      limit: RECENT_SERVICES_DISPLAY_COUNT,
    },
  })

  const company = data?.company
  const apps = company?.apps?.items?.filter(isPresent) ?? []
  const databases = company?.databases?.items?.filter(isPresent) ?? []
  const staticSites = company?.staticSites?.items?.filter(isPresent) ?? []
  const objectStorages = company?.objectStorages?.items?.filter(isPresent) ?? []
  const loadBalancers =
    company?.edgeLoadBalancers?.items?.filter(isPresent) ?? []

  const services = [
    ...apps,
    ...databases,
    ...staticSites,
    ...objectStorages,
    ...loadBalancers,
  ]
    .sort((a, b) => {
      const firstValue = a?.updatedAt ?? 0
      const secondValue = b?.updatedAt ?? 0

      return new Date(secondValue).getTime() - new Date(firstValue).getTime()
    })
    .filter(isPresent)

  const recentServices = services.slice(0, RECENT_SERVICES_DISPLAY_COUNT)

  if (!recentServices.length) {
    return null
  }

  return (
    <RecentServicesGrid>
      {recentServices.map((service, idx) => {
        const hasIsSuspended = 'isSuspended' in service
        const serviceStatus =
          (isCompanySuspended || (hasIsSuspended && service.isSuspended)
            ? 'suspended'
            : 'status' in service
              ? service?.status
              : undefined) ?? undefined
        return service?.__typename === 'App' ? (
          <Link
            key={service?.id ?? idx}
            to={getCompanyPath({
              pathname: '/app/:appName/overview',
              variables: { appName: service?.name ?? '' },
            })}
          >
            <ServiceCard
              type="app"
              name={service?.displayName ?? ''}
              info={getAppInfo(service)}
              status={serviceStatus}
              disabled={serviceStatus === 'deleting'}
            />
          </Link>
        ) : service?.__typename === 'Database' ? (
          <Link
            key={service?.id ?? idx}
            to={getCompanyPath({
              pathname: '/database/:databaseName/overview',
              variables: { databaseName: service?.name ?? '' },
            })}
          >
            <ServiceCard
              type="db"
              name={service?.displayName ?? ''}
              info={
                <Suspense fallback="Loading database info...">
                  <DatabaseInfo service={service} />
                </Suspense>
              }
              status={serviceStatus}
              disabled={serviceStatus === 'deleting'}
            />
          </Link>
        ) : service?.__typename === 'ObjectStorage' ? (
          <Link
            key={service?.id ?? idx}
            to={getCompanyPath({
              pathname: '/object-storage/:idObjectStorage/objects',
              variables: { idObjectStorage: service?.id ?? '' },
            })}
          >
            <ServiceCard
              type="objectStorage"
              name={service?.displayName ?? ''}
              info={service?.name ?? ''}
            />
          </Link>
        ) : service?.__typename === 'EdgeLoadBalancer' ? (
          <Link
            key={service?.id ?? idx}
            to={getCompanyPath({
              pathname: '/load-balancer/:loadBalancerName/overview',
              variables: { loadBalancerName: service?.name ?? '' },
            })}
          >
            <ServiceCard
              type="loadBalancer"
              name={service?.displayName ?? ''}
              info={service?.name ?? ''}
            />
          </Link>
        ) : service?.__typename === 'StaticSite' ? (
          <Link
            key={service?.id ?? idx}
            to={getCompanyPath({
              pathname: '/static-site/:staticSiteName/overview',
              variables: { staticSiteName: service?.name ?? '' },
            })}
          >
            <ServiceCard
              type="staticSite"
              name={service?.displayName ?? ''}
              info={getStaticSiteInfo(service)}
              status={serviceStatus}
              disabled={serviceStatus === 'deleting'}
            />
          </Link>
        ) : null
      })}
    </RecentServicesGrid>
  )
}

export const RecentServices = () => {
  return (
    <QueryErrorBoundary
      queries={[RECENT_SERVICES_QUERY]}
      fallback={<RecentServicesFallback />}
    >
      <RecentServicesData />
    </QueryErrorBoundary>
  )
}
